import React from 'react';
import {useNavigate} from 'react-router-dom';
import {formatDurationCompact} from '../lib/audioMetadata';
import Modal from './workspace/Modal';

interface UpgradeRequiredModalProps {
  isOpen: boolean;
  /** The message the API sent — it names the actual limit, so it is shown rather than paraphrased. */
  message: string;
  /** Seconds. The clip the user tried to upload or transcribe. */
  duration?: number;
  /** Seconds. What their plan allows — max_file_seconds for `file_too_long`, seconds_limit for
   *  `period_limit_exceeded`. Never a hardcoded number: always shown as the value the server sent. */
  limit?: number;
  requiredPlan?: string;
  /**
   * `file_too_long` (this one file will never work on this plan) and `period_limit_exceeded` (out of
   * minutes this period, the file itself is fine) need different framing — a `period_limit_exceeded`
   * 402 only ever comes from `/transcribe`, so the audio is already uploaded, unlike `file_too_long`
   * from `/audio/upload-url`. Undefined falls back to the `file_too_long` copy, since that is the
   * paywall people hit before ever reaching this modal from anywhere else.
   */
  reason?: string;
  onClose: () => void;
}

/**
 * The 402 wall.
 *
 * The per-file and per-period ceilings come from the plan and both answer with this modal, but they
 * are not the same problem: a `file_too_long` file will never go through on this plan no matter what,
 * while `period_limit_exceeded` just means the balance is spent until the period rolls (or an upgrade
 * raises it) — the file itself may be perfectly fine. Collapsing them into one message is what let a
 * mid-period rejection claim a file "was not uploaded" when `/transcribe` only ever answers this after
 * the upload already succeeded.
 */
export const UpgradeRequiredModal: React.FC<UpgradeRequiredModalProps> = ({
  isOpen,
  message,
  duration,
  limit,
  requiredPlan,
  reason,
  onClose,
}) => {
  const navigate = useNavigate();
  const isPeriodLimit = reason === 'period_limit_exceeded';

  return (
    <Modal
      isOpen={isOpen}
      title={isPeriodLimit ? 'You are out of transcription time this period' : 'This file is longer than your plan allows'}
      subtitle={message}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-1.5 text-sm text-gray-400 hover:bg-gray-800
             hover:text-gray-100 transition-colors cursor-pointer"
          >
            Not now
          </button>
          <button
            type="button"
            onClick={() => navigate('/account')}
            className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white
             hover:bg-indigo-500 transition-colors cursor-pointer"
          >
            See plans
          </button>
        </>
      }
    >
      {duration !== undefined && limit !== undefined && (
        <div className="mb-4 grid grid-cols-2 gap-3">
          <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
            <p className="text-xs text-gray-500">{isPeriodLimit ? 'This file' : 'Your file'}</p>
            <p className="mt-0.5 text-lg tabular-nums text-amber-300">{formatDurationCompact(duration)}</p>
          </div>
          <div className="rounded-lg border border-gray-800 bg-gray-950/50 p-3">
            <p className="text-xs text-gray-500">{isPeriodLimit ? 'Left this period' : 'Your limit'}</p>
            <p className="mt-0.5 text-lg tabular-nums text-gray-300">{formatDurationCompact(limit)}</p>
          </div>
        </div>
      )}

      <p className="text-sm text-gray-400">
        {isPeriodLimit ? (
          <>
            {requiredPlan
              ? `Upgrading to ${requiredPlan} raises how much you get each period`
              : 'Your balance resets at the start of your next billing period'}
            {' '}— the audio is already uploaded, so nothing needs to be sent again once you can
            transcribe it.
          </>
        ) : (
          <>
            {requiredPlan
              ? `Upgrading to ${requiredPlan} raises the per-file limit`
              : 'Upgrading raises the per-file limit'}
            {' '}— your file was not uploaded, so nothing was wasted.
          </>
        )}
      </p>
    </Modal>
  );
};

export default UpgradeRequiredModal;
